// The one HTTP client (docs/api/API-CONTRACT.md, modules-and-api.md §0.3): same-origin cookie session, X-Request-Id on every
// call, X-Branch-Id from the active branch, Idempotency-Key on the POSTs the contract marks `key`, If-Match from the last ETag,
// X-StepUp-Token when an action was authorised, problem+json into ApiError, and every response validated with the contract's
// Zod schema, the same schema the Worker validated with (TEN-73). D13: while offline, writes are refused before fetch.
import type { z } from "zod";
import { isOnline } from "../lib/online";
import { ulid } from "../lib/ulid";
import { ApiError, NetworkError, problemFrom } from "./problem";

export const API_BASE = "/api/v1";

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions<S extends z.ZodType | undefined> {
  method?: Method | undefined;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null> | undefined;
  /** The contract's response schema. Omit only for 204 answers. */
  schema?: S | undefined;
  /** `key` routes: a fresh ULID per logical request; pass the same key to retry the same request. */
  idempotencyKey?: string | undefined;
  /** Versioned mutations: the ETag read earlier. */
  ifMatch?: string | undefined;
  stepUpToken?: string | undefined;
  /** Override the active branch for this call. `null` sends no header. */
  branchId?: string | null | undefined;
  /** Extra request headers a route needs — the kiosk's `X-Handover-Token` (UC-PATIENTS-023). Never a header the client sets itself. */
  headers?: Record<string, string> | undefined;
  signal?: AbortSignal | undefined;
}

export interface ApiResult<T> {
  data: T;
  status: number;
  etag: string | null;
  requestId: string;
  headers: Headers;
}

type Infer<S> = S extends z.ZodType ? z.output<S> : undefined;

let activeBranchId: string | null = null;
export const setActiveBranchId = (id: string | null): void => { activeBranchId = id; };
export const getActiveBranchId = (): string | null => activeBranchId;

let lastOkResponseAt = 0;
/** When the server last answered 2xx, in ms since the epoch (0 before the first). Every answered request extends the server's idle expiry; the idle lock models that from here (UC-IDENTITY-003 A1). */
export const lastSuccessfulResponseAt = (): number => lastOkResponseAt;

type AuthListener = (problem: ApiError) => void;
const authListeners = new Set<AuthListener>();
/** Session-level failures (401) are broadcast so the session store can show the lock screen or sign-in. */
export function onAuthProblem(l: AuthListener): () => void { authListeners.add(l); return () => { authListeners.delete(l); }; }

export function buildUrl(path: string, query?: RequestOptions<undefined>["query"]): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  const s = qs.toString();
  return s ? `${url}?${s}` : url;
}

export async function request<S extends z.ZodType | undefined = undefined>(path: string, opts: RequestOptions<S> = {}): Promise<ApiResult<Infer<S>>> {
  const method: Method = opts.method ?? (opts.body !== undefined ? "POST" : "GET");
  const mutating = method !== "GET";
  if (mutating && !isOnline()) throw new NetworkError("offline", true);
  const requestId = ulid();
  const headers = new Headers({ Accept: "application/json, application/problem+json", "X-Request-Id": requestId });
  if (opts.body !== undefined) headers.set("Content-Type", "application/json");
  const branch = opts.branchId === undefined ? activeBranchId : opts.branchId;
  if (branch) headers.set("X-Branch-Id", branch);
  if (opts.idempotencyKey) headers.set("Idempotency-Key", opts.idempotencyKey);
  if (opts.ifMatch) headers.set("If-Match", opts.ifMatch);
  if (opts.stepUpToken) headers.set("X-StepUp-Token", opts.stepUpToken);
  // An extra header never replaces one the client set itself (request id, branch, idempotency key, If-Match, step-up token).
  if (opts.headers) for (const [k, v] of Object.entries(opts.headers)) if (!headers.has(k)) headers.set(k, v);

  const init: RequestInit = { method, headers, credentials: "same-origin", cache: "no-store" };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;

  // The answer's body is read inside the same catch as the request: an answer whose headers came and whose body was cut off is lost
  // as one that never arrived — a NetworkError, so a screen keeps its key and says the write may have landed (the verification of
  // round 3, 25 Oct 2026; TC-IDENTITY-116). Any failure once the request is handed to fetch is a lost answer, never "offline",
  // whatever the device reports by then: the connection that drops mid-request is what takes the device offline, so the write may
  // well have landed, and only the guard above, which sends nothing, may say nothing was sent (BR-TENANCY-031; the closing check of
  // 25 Oct 2026, MX-2 and CHKF-merge-2; TC-IDENTITY-118, TC-IDENTITY-119).
  let res: Response;
  let text: string;
  try {
    res = await fetch(buildUrl(path, opts.query), init);
    text = await res.text();
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new NetworkError(err instanceof Error ? err.message : "network failure", false);
  }
  const echoedId = res.headers.get("X-Request-Id") ?? requestId;
  let body: unknown = undefined;
  if (text) { try { body = JSON.parse(text); } catch { body = text; } }
  if (!res.ok) {
    const problem = problemFrom(res.status, body, echoedId);
    if (problem.status === 401) for (const l of authListeners) l(problem);
    throw problem;
  }
  lastOkResponseAt = Date.now();
  let data: unknown = body;
  if (opts.schema) {
    const parsed = opts.schema.safeParse(body);
    if (!parsed.success) {
      const issues = parsed.error.issues.slice(0, 5).map((i) => `${i.path.join(".") || "$"}: ${i.message}`).join("; ");
      throw new ApiError({ type: "about:blank", title: "Response did not match the contract", status: res.status, code: "INTERNAL_ERROR", requestId: echoedId, detail: `${method} ${path}: ${issues}` });
    }
    data = parsed.data;
  }
  return { data: data as Infer<S>, status: res.status, etag: res.headers.get("ETag"), requestId: echoedId, headers: res.headers };
}

export const newIdempotencyKey = (): string => ulid();

/** Sign-out purges everything the service worker cached (BR-IDENTITY-005). */
export async function purgeCaches(): Promise<void> {
  if (typeof caches === "undefined") return;
  try { for (const k of await caches.keys()) if (k.startsWith("vx-api")) await caches.delete(k); } catch { /* nothing to purge */ }
}
